import { useState, useEffect } from 'react';
import { repositoryFactory } from '@data/repositories/RepositoryFactory.js';

/**
 * Hook for managing and viewing user progress
 */
export function useUserProgress(userId, courseId) {
  const [progress, setProgress] = useState(null);
  const [moduleProgress, setModuleProgress] = useState([]);
  const [questionProgress, setQuestionProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const progressRepo = repositoryFactory.getProgressRepository();
  const courseRepo = repositoryFactory.getCourseRepository();

  /**
   * Load user progress data
   */
  const loadProgress = async () => {
    if (!userId || !courseId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get user's progress
      const userProgress = await progressRepo.getUserProgress(userId, courseId);
      setProgress(userProgress);

      if (userProgress) {
        // Get course structure to map progress
        const course = await courseRepo.getCourseById(courseId);
        
        if (course) {
          // Build module progress summary
          const moduleSummary = course.sections.map(section => {
            const sectionProg = userProgress.getSectionProgress(section.id);
            return {
              moduleId: section.id,
              moduleName: section.title,
              isCompleted: userProgress.isSectionCompleted(section.id),
              isUnlocked: userProgress.isSectionUnlocked(section.id),
              score: sectionProg?.score || 0,
              totalQuestions: section.questions?.length || 0,
              completedQuestions: sectionProg?.completedQuestions || 0,
              lastActivity: sectionProg?.lastAttemptAt || null,
            };
          });
          setModuleProgress(moduleSummary);

          // Build question progress detail
          const questionDetails = [];
          course.sections.forEach(section => {
            section.questions?.forEach(question => {
              const qProg = userProgress.getQuestionProgress(question.id);
              if (qProg) {
                questionDetails.push({
                  questionId: question.id,
                  moduleId: section.id,
                  moduleName: section.title,
                  questionText: question.text,
                  questionType: question.type,
                  attempts: qProg.attempts || 0,
                  correctAttempts: qProg.correctAttempts || 0,
                  lastAttemptAt: qProg.lastAttemptAt || null,
                  isCorrect: qProg.isCorrect || false,
                  masteryLevel: qProg.masteryLevel || 0,
                });
              }
            });
          });
          setQuestionProgress(questionDetails);
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading user progress:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get overall statistics
   */
  const getStatistics = () => {
    if (!progress || !moduleProgress.length) {
      return {
        overallScore: 0,
        completedModules: 0,
        totalModules: 0,
        totalAttempts: 0,
        correctAttempts: 0,
      };
    }

    return {
      overallScore: progress.overallScore || 0,
      completedModules: moduleProgress.filter(m => m.isCompleted).length,
      totalModules: moduleProgress.length,
      totalAttempts: questionProgress.reduce((sum, q) => sum + q.attempts, 0),
      correctAttempts: questionProgress.reduce((sum, q) => sum + q.correctAttempts, 0),
    };
  };

  /**
   * Get progress by module
   */
  const getModuleProgress = (moduleId) => {
    return moduleProgress.find(m => m.moduleId === moduleId);
  };

  /**
   * Get questions for a specific module
   */
  const getModuleQuestions = (moduleId) => {
    return questionProgress.filter(q => q.moduleId === moduleId);
  };

  // Load progress when userId or courseId changes
  useEffect(() => {
    loadProgress();
  }, [userId, courseId]);

  return {
    progress,
    moduleProgress,
    questionProgress,
    loading,
    error,
    statistics: getStatistics(),
    getModuleProgress,
    getModuleQuestions,
    refresh: loadProgress,
  };
}
